"use client";

import Link from "next/link";
import Image from "next/image";
import type { ComponentProps } from "react";
import { storyblokEditable } from "@storyblok/react/rsc";
import { track } from "@vercel/analytics";
import { trackEvent } from "@/lib/analytics";
import { useMembership } from "@/lib/hooks/useMembership";

import { Badge } from "@/components/ui/badge";
import type { AdStoryblok, CampaignStoryblok } from "@/component-types-sb";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";

type AdProps = {
  blok: AdStoryblok;
  campaign?: CampaignStoryblok;
  label?: string;
} & ComponentProps<"div">;

const Ad = ({ blok, campaign, label = "Werbung", ...rest }: AdProps) => {
  const { isMember } = useMembership();

  if (isMember) return null;

  const ad = (blok.ad as AdStoryblok | undefined) || blok;

  if (!ad?.content) {
    return null;
  }

  return (
    <div className={rest.className}>
      <Badge variant="secondary" className="font-normal mb-1">
        {label}
      </Badge>
      <Link
        {...storyblokEditable(ad)}
        href={getUrlFromLinkObject(ad.content?.link) || "#"}
        target={ad.content?.link?.target || "_self"}
        data-ad-name={ad.name}
        data-ad-uid={ad._uid}
        data-ad-campaign={campaign?.name}
        data-ad-url={ad.content?.link?.cached_url}
        onClick={() => {
          track("Ad click", {
            uid: ad._uid,
            name: ad.name,
            campaign: campaign?.name,
          });
          trackEvent("ad_clicked", {
            name: ad.name,
            campaign: campaign?.name,
            url: ad.content?.link?.cached_url,
          });
        }}
        aria-label="Werbung anzeigen"
      >
        {ad.content?.image && (
          <Image
            src={ad.content?.image.filename}
            alt={ad.content?.image.alt || ""}
            sizes="(max-width: 768px) 100vw, 70vw"
            className="w-full h-auto object-contain"
            width={6600}
            height={1070}
          />
        )}
      </Link>
    </div>
  );
};

export default Ad;
